import React, { useState, useRef, useEffect } from 'react'
import SummaryBarGraph from '../../graph/SummaryBarGraph'
import Loading from '../../Loading'
import AnnualQuarterSwitch from './AnnualQuarterSwitch'
import SummaryQuarterlyBarGraph from '../../graph/SummaryQuarterlyBarGraph'
import { currencyToSymbol } from "../../utils/commonUtils";
import TimeRangeContainer from '../../utils/TimeRangeContainer'
import AxisSettingButton from '../../utils/AxisSettingButton'

// onPeriodChangeやlabel Stateを引数にしているのは、SummaryTab画面に保存ボタンがあるから.
const SummaryGraph = ({ financialData, quarterlyFinancialData, dividendData, account, unit='value', onPeriodChange, labelState, setLabelState, selectedTimeRange, setSelectedTimeRange, period, setLoaded }) => {
    let modifiedUnit;
    if (unit === 'value') {
        modifiedUnit = currencyToSymbol(financialData[financialData.length - 1]?.reported_currency);
    } else {
        modifiedUnit = unit;
    }
    
    const [selectedRange, setSelectedRange] = useState(selectedTimeRange);
    const [selectedPeriod, setSelectedPeriod] = useState(period);
    const [width, setWidth] = useState(0); // 幅を管理する状態
    const [state, setState] = useState(labelState)
    const ref = useRef(null);

    const timeRanges = [
        { label: '3Y', value: '3Y' },
        { label: '5Y', value: '5Y' },
        { label: '10Y', value: '10Y' },
        { label: '20Y', value: '20Y' },
        { label: 'MAX', value: 'MAX' },
    ];

    const makeDisplayData = (data) => {
        if (!data || data.length === 0) return [];
        switch (selectedRange) {
            case '3Y': return data.slice(Math.max(data.length - 3, 0));
            case '5Y': return data.slice(Math.max(data.length - 5, 0));
            case '10Y': return data.slice(Math.max(data.length - 10, 0));
            case '20Y': return data.slice(Math.max(data.length - 20, 0));
            case 'MAX': default: return data;
        }
    };

    const makeDisplayQuarterlyData = (data) => {
        if (!data || data.length === 0) return [];
        const latestYear = parseInt(data[data.length - 1].x.slice(0, 4));

        switch (selectedRange) {
            case '3Y': 
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 3);
            case '5Y':
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 5);;
            case '10Y':
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 10);
            case '20Y':
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 20);
            case 'MAX': default: return data;
        }
    }

    const makeDisplayDividendData = (data) => {
        if (!data || data.length === 0) return data;
        const latestYear = parseInt(data[data.length - 1].x.slice(0, 4));
        switch (selectedRange) {
            case '3Y': 
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 3);
            case '5Y':
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 5);;
            case '10Y':
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 10);
            case '20Y':
                return data.filter((dt) => (parseInt(dt.x.slice(0, 4))) >= latestYear - 20);
            case 'MAX': default: return data;
        }
    }

    
    useEffect(() => {
        setLoaded((prev) => !prev)
    }, [])
    
    
    // containerのサイズをグラフに渡すため 
    useEffect(() => {
        if (!ref.current) return;

        // ResizeObserverを定義
        const observer = new ResizeObserver((entries) => {
        for (let entry of entries) {
            if (entry.contentRect) {
            setWidth(entry.contentRect.width); // ref要素の幅を設定
            }
        }
        });

        // ref要素を監視
        observer.observe(ref.current);

        // クリーンアップ処理
        return () => {
        if (ref.current) {
            observer.unobserve(ref.current);
        }
        };
    }, []);

    if (!financialData) {
        return <Loading />;
    }

    if (['payout_ratio', 'dividend'].includes(account) && !dividendData) {
        return <Loading />
    }

    let annualFinancialData;
    let timeSeriesDividendData;
    if (account === 'dividend') {
        if (dividendData) {
            timeSeriesDividendData = dividendData.map((data) => ({
                x: data['fiscal_year'] + '年',
                y: data['adjdividend'],
            } ));
        }
    } else if (account === 'payout_ratio') {
        if (dividendData) {
            timeSeriesDividendData = dividendData.map((data) => ({
                x: data['fiscal_year'] + '年',
                y: data[account],
            } ));
        }
    } else {
        annualFinancialData = financialData.map((data) => ({
            x: data['fiscal_year'] + '年',
            y: data[account],
        }));
    }
    
    let quarterlyData;
    if (quarterlyFinancialData) {
        quarterlyData = quarterlyFinancialData.map((data) => ({
            x: data['fiscal_year'] + '年 ' + data['type_of_period'],
            y: data[account],
        }));
    }

    const handlePeriodChange = (period) => {
        setSelectedPeriod(period);
        onPeriodChange(period);
    };


    const handleTimeRangeSetting = (newRange) => {
        setSelectedRange(newRange);
        setSelectedTimeRange(newRange);
    }

    const handleLabelSettingClick = (newState) => {
        setState(newState);
        setLabelState(newState);
    }

    return (
        <>
            <div className="flex gap-2 items-center" ref={ref}>
                <div className='mb-4'>
                    <TimeRangeContainer timeRanges={timeRanges} handleTimeRangeSetting={handleTimeRangeSetting} selectedRange={selectedTimeRange} />
                </div>
                {account !== 'dividend' &&
                    <AnnualQuarterSwitch onPeriodChange={handlePeriodChange} height="28px" period={period}/>
                }
                <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick}  labelState={state} /> 
            </div>
            {(['dividend', 'payout_ratio'].includes(account)) ?
                <SummaryBarGraph data={makeDisplayDividendData(timeSeriesDividendData)} unit={modifiedUnit} width={width} showLabel={state} />
            :
            (selectedPeriod === 'annual') ? (
                annualFinancialData.length > 0 ?
                <SummaryBarGraph data={makeDisplayData(annualFinancialData)} unit={modifiedUnit} width={width} showLabel={state} /> : null
            ) : (
                quarterlyData &&
                    <SummaryQuarterlyBarGraph data={makeDisplayQuarterlyData(quarterlyData)} unit={modifiedUnit} width={width} showLabel={state} />
            )}
            
        </>
    );
};

export default SummaryGraph;
